import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Folder, FolderOpen, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SCAN_MAX_DEPTH, emptyListingText, entryStatus, folderDisplayPath, folderLocation, folderStatusLines, formatSize } from '@cerebro-diem/core';
import type { BindableKind, ConnectorRoot, FolderBinding } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import {
  linkFolder,
  relinkFolder,
  unlinkFolder,
  useConnectorRoots,
  useConnectors,
  useFolderBindings,
  usePickerFolders,
} from '../../services/connectors';
import type { PickerFolder } from '../../services/connectors';
import { inputClass, outlineButton, primaryButton } from '../../components/styles';

const ago = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true });
const modified = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');

/**
 * "Linked folder" on a Project, Area or Resource page: the folders its owner
 * linked it to, each on one of their computers, with what the latest scan
 * found in it. Links are made only here, by hand, from a scan's folders;
 * nothing is linked by name, and nothing on the computer changes. A folder
 * is shown as its root's label and the names below it, never as a path.
 */
export default function LinkedFolder({ kind, entityId }: { kind: BindableKind; entityId: string }) {
  const bindings = useFolderBindings(kind, entityId);
  const [picking, setPicking] = useState(false);

  return (
    <section className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm space-y-3" aria-labelledby="linked-folder">
      <h2 id="linked-folder" className="text-lg font-semibold">
        Linked folder
      </h2>
      {bindings.isError ? (
        <p className="text-sm text-error">Could not load the linked folder: {errorMessage(bindings.error)}</p>
      ) : bindings.isPending ? (
        <p className="text-sm text-on-surface-variant">Loading…</p>
      ) : (
        <>
          {bindings.data.length === 0 && !picking && <p className="text-sm text-on-surface-variant">No folder is linked.</p>}
          {bindings.data.map((binding) => (
            <BoundFolder key={binding.id} binding={binding} kind={kind} entityId={entityId} />
          ))}
        </>
      )}
      {picking ? (
        <FolderPicker kind={kind} entityId={entityId} onDone={() => setPicking(false)} />
      ) : (
        <button type="button" onClick={() => setPicking(true)} className={`${outlineButton} inline-flex items-center gap-1`}>
          <FolderOpen className="w-4 h-4" aria-hidden="true" /> Link a folder
        </button>
      )}
    </section>
  );
}

function BoundFolder({ binding, kind, entityId }: { binding: FolderBinding; kind: BindableKind; entityId: string }) {
  const queryClient = useQueryClient();
  const [repairing, setRepairing] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.folderBindings(kind, entityId) });
  const unlink = useMutation({ mutationFn: () => unlinkFolder(binding.id), onSuccess: refresh });
  // Linking the same folder again records it as it is now: the owner says it is the same folder.
  const confirm = useMutation({ mutationFn: () => relinkFolder(binding.id, binding.folder_stable_id), onSuccess: refresh });
  const shown = folderDisplayPath(binding);
  const settled = binding.folder_seen_in_last_scan && !binding.folder_unreadable && !binding.needs_repair && !binding.folder_changed;
  const empty = emptyListingText(binding);
  return (
    <div className="rounded-xl bg-surface-variant/30 p-3 space-y-2" aria-label={`Folder ${shown}`}>
      <div className="flex flex-wrap items-center gap-2">
        <FolderOpen className="w-5 h-5 text-primary" aria-hidden="true" />
        <p className="font-medium break-all">{shown}</p>
        <button
          type="button"
          onClick={() => unlink.mutate()}
          disabled={unlink.isPending}
          className={`${outlineButton} ml-auto`}
          aria-label={`Unlink ${shown}`}
        >
          Unlink
        </button>
      </div>
      <p className="text-xs text-on-surface-variant">{folderLocation(binding.connector_name)}</p>
      {folderStatusLines(binding, ago).map((line) => (
        <p key={line} className={`text-sm ${settled ? 'text-on-surface-variant' : 'text-tertiary'}`}>
          {line}
        </p>
      ))}
      {binding.folder_changed && (
        <button type="button" onClick={() => confirm.mutate()} disabled={confirm.isPending} className={primaryButton} aria-label={`Confirm ${shown} is the same folder`}>
          It's the same folder
        </button>
      )}
      {binding.needs_repair && !repairing && (
        <button type="button" onClick={() => setRepairing(true)} className={primaryButton}>
          Link again
        </button>
      )}
      {repairing && <FolderPicker kind={kind} entityId={entityId} repair={binding} onDone={() => setRepairing(false)} />}
      {empty !== null ? (
        <p className="text-sm text-on-surface-variant">{empty}</p>
      ) : binding.entries.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-on-surface-variant">
                <th className="font-normal py-1 pr-3">Name</th>
                <th className="font-normal py-1 pr-3">Size</th>
                <th className="font-normal py-1">Modified</th>
              </tr>
            </thead>
            <tbody>
              {binding.entries.map((entry) => {
                const note = entryStatus(entry, ago);
                const Icon = entry.kind === 'dir' ? Folder : FileText;
                return (
                  <tr key={entry.stable_id} className="align-top">
                    <td className="py-1 pr-3">
                      <span className="inline-flex items-center gap-1 break-all">
                        <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                        {entry.name}
                      </span>
                      {note && <span className="block text-xs text-tertiary">{note}</span>}
                    </td>
                    <td className="py-1 pr-3 whitespace-nowrap">{formatSize(entry.size)}</td>
                    <td className="py-1 whitespace-nowrap">{modified(entry.modified_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {binding.entries.length >= 500 && <p className="text-xs text-tertiary">Showing the first 500 entries.</p>}
      {unlink.isError && <p className="text-sm text-error">Not unlinked: {errorMessage(unlink.error)}</p>}
      {confirm.isError && <p className="text-sm text-error">Not confirmed: {errorMessage(confirm.error)}</p>}
    </div>
  );
}

/**
 * Choose a folder from the latest good scan of an approved root, one level
 * at a time. With `repair`, only that link's root is offered, and choosing a
 * folder links that link again rather than adding another.
 */
function FolderPicker({ kind, entityId, repair, onDone }: { kind: BindableKind; entityId: string; repair?: FolderBinding; onDone: () => void }) {
  const queryClient = useQueryClient();
  const connectors = useConnectors();
  const roots = useConnectorRoots();
  // Only a connector that is not revoked AND still holds a key can report a
  // folder, so only those are offered to link to.
  const live = new Map(
    (connectors.data ?? []).filter((c) => c.revoked_at === null && c.key_id !== null).map((c) => [c.connector_id, c.name]),
  );
  const choices = (roots.data ?? []).filter(
    (root) =>
      root.approved_at !== null &&
      root.last_ok_scan_at !== null &&
      live.has(root.connector_id) &&
      (!repair || (root.connector_id === repair.connector_id && root.root_key === repair.root_key)),
  );
  const [chosen, setChosen] = useState<string>('');
  const root: ConnectorRoot | null = choices.find((r) => `${r.connector_id}:${r.root_key}` === chosen) ?? choices[0] ?? null;
  const [trail, setTrail] = useState<PickerFolder[]>([]);
  const parent = trail.length ? trail[trail.length - 1].stable_id : null;
  const folders = usePickerFolders(root, parent);
  const link = useMutation({
    mutationFn: (folder: PickerFolder) => {
      if (!root) throw new Error('choose a folder first');
      return repair ? relinkFolder(repair.id, folder.stable_id) : linkFolder(kind, entityId, root, folder.stable_id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.folderBindings(kind, entityId) });
      onDone();
    },
  });

  if (connectors.isError || roots.isError) {
    return <p className="text-sm text-error">Could not load your folders: {errorMessage(connectors.error ?? roots.error)}</p>;
  }
  if (connectors.isPending || roots.isPending) return <p className="text-sm text-on-surface-variant">Loading…</p>;
  if (!root) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-on-surface-variant">
          No approved folders have been scanned yet. Pair a computer and approve its folders in{' '}
          <Link to="/settings" className="text-primary hover:underline">
            Settings
          </Link>
          , then run a scan on that computer.
        </p>
        <button type="button" onClick={onDone} className={outlineButton}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-outline/30 p-3 space-y-3" aria-label={repair ? 'Link the folder again' : 'Choose a folder'}>
      <select
        className={`${inputClass} w-auto`}
        aria-label="Folder on"
        value={`${root.connector_id}:${root.root_key}`}
        onChange={(e) => {
          setChosen(e.target.value);
          setTrail([]);
        }}
      >
        {choices.map((r) => (
          <option key={`${r.connector_id}:${r.root_key}`} value={`${r.connector_id}:${r.root_key}`}>
            {live.get(r.connector_id)} · {r.label}
          </option>
        ))}
      </select>
      <p className="text-xs text-on-surface-variant">From the scan {ago(root.last_ok_scan_at!)}.</p>
      <nav className="flex flex-wrap items-center gap-1 text-sm" aria-label="Where you are">
        <button type="button" onClick={() => setTrail([])} className="text-primary hover:underline">
          {root.label}
        </button>
        {trail.map((folder, index) => (
          <span key={folder.stable_id} className="inline-flex items-center gap-1">
            ›
            <button type="button" onClick={() => setTrail(trail.slice(0, index + 1))} className="text-primary hover:underline">
              {folder.name}
            </button>
          </span>
        ))}
      </nav>
      {folders.isError ? (
        <p className="text-sm text-error">Could not load the folders: {errorMessage(folders.error)}</p>
      ) : folders.isPending ? (
        <p className="text-sm text-on-surface-variant">Loading…</p>
      ) : folders.data.length === 0 ? (
        <p className="text-sm text-on-surface-variant">No folders to link here in the latest scan.</p>
      ) : (
        <ul className="space-y-1">
          {folders.data.map((folder) => {
            // What is inside a folder one level above the last is listed, but its own folders cannot be linked.
            const opens = folder.depth < SCAN_MAX_DEPTH - 1;
            return (
              <li key={folder.stable_id} className="flex items-center gap-2">
                {opens ? (
                  <button
                    type="button"
                    onClick={() => setTrail([...trail, folder])}
                    className="inline-flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-1 text-left hover:bg-surface-variant/60"
                    aria-label={`Open ${folder.name}`}
                  >
                    <Folder className="w-4 h-4 shrink-0" aria-hidden="true" />
                    <span className="break-all">{folder.name}</span>
                  </button>
                ) : (
                  <span className="inline-flex min-w-0 flex-1 items-center gap-2 px-2 py-1">
                    <Folder className="w-4 h-4 shrink-0" aria-hidden="true" />
                    <span className="break-all">{folder.name}</span>
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => link.mutate(folder)}
                  disabled={link.isPending}
                  className={primaryButton}
                  aria-label={`Link ${folder.name}`}
                >
                  Link
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {link.isError && <p className="text-sm text-error">Not linked: {errorMessage(link.error)}</p>}
      <button type="button" onClick={onDone} className={outlineButton}>
        Cancel
      </button>
    </div>
  );
}
